<script setup lang="ts">
import type { Product, ProductUsagePrice } from '@/types'
import { Form } from '@inertiajs/vue3'
import { ref } from 'vue'
import { firstFormError } from '@/lib/forms'

const props = defineProps<{
  product: Partial<Product>
  action: string
  method: 'post' | 'patch'
  submitLabel: string
}>()

type EditableUsagePrice = ProductUsagePrice & { localId: string }

let nextUsagePriceId = 0
const newLocalId = () => `new-${nextUsagePriceId++}`

const usagePrices = ref<EditableUsagePrice[]>(
  (props.product.usage_prices ?? []).map(price => ({
    ...price,
    localId: String(price.id ?? newLocalId()),
  })),
)
const removedUsagePriceIds = ref<number[]>([])

function addUsagePrice() {
  usagePrices.value.push({
    localId: newLocalId(),
    kind: '',
    unit_price_cents: '',
  })
}

function removeUsagePrice(index: number) {
  const [removed] = usagePrices.value.splice(index, 1)

  if (removed?.id)
    removedUsagePriceIds.value.push(removed.id)
}
</script>

<template>
  <Form
    v-slot="{ errors, processing }"
    :action="props.action"
    :method="props.method"
    :options="{ preserveScroll: true }"
    class="max-w-2xl space-y-5"
  >
    <UFormField
      label="Name"
      name="name"
      description="Human-friendly product name."
      :error="firstFormError(errors.name)"
      required
    >
      <UInput
        id="name"
        name="name"
        :default-value="props.product.name"
        placeholder="Acme Server Agent"
        icon="i-lucide-box"
        class="w-full"
      />
    </UFormField>

    <UFormField
      label="Slug"
      name="slug"
      description="Stable identifier used in URLs and the update API. Lowercase letters, numbers, and hyphens."
      :error="firstFormError(errors.slug)"
      required
    >
      <UInput
        id="slug"
        name="slug"
        :default-value="props.product.slug"
        placeholder="acme-server-agent"
        icon="i-lucide-hash"
        class="w-full font-mono"
      />
    </UFormField>

    <UFormField
      label="Description"
      name="description"
      :error="firstFormError(errors.description)"
    >
      <UTextarea
        id="description"
        name="description"
        :default-value="props.product.description ?? ''"
        :rows="4"
        placeholder="What this product is and who hosts it."
        class="w-full"
      />
    </UFormField>

    <div class="space-y-3">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h2 class="text-highlighted text-sm font-medium">
            Usage pricing
          </h2>
          <p class="text-muted text-sm">
            Current price hints by event kind, used later when exporting invoices.
          </p>
        </div>

        <UButton
          type="button"
          label="Add price"
          icon="i-lucide-plus"
          color="neutral"
          variant="subtle"
          @click="addUsagePrice"
        />
      </div>

      <div
        v-if="usagePrices.length > 0"
        class="ring-default divide-default overflow-hidden rounded-lg ring divide-y"
      >
        <div
          v-for="(price, index) in usagePrices"
          :key="price.localId"
          class="grid gap-3 p-4 sm:grid-cols-[1fr_9rem_auto]"
        >
          <input
            v-if="price.id"
            type="hidden"
            :name="`usage_prices_attributes[${index}][id]`"
            :value="price.id"
          >

          <UFormField
            label="Kind"
            :name="`usage_prices_attributes[${index}][kind]`"
            :error="firstFormError(errors[`usage_prices.kind`])"
          >
            <UInput
              v-model="price.kind"
              :name="`usage_prices_attributes[${index}][kind]`"
              placeholder="invoice_processed"
              icon="i-lucide-tag"
              class="w-full font-mono"
            />
          </UFormField>

          <UFormField
            label="Unit cents"
            :name="`usage_prices_attributes[${index}][unit_price_cents]`"
            :error="firstFormError(errors[`usage_prices.unit_price_cents`])"
          >
            <UInput
              v-model="price.unit_price_cents"
              :name="`usage_prices_attributes[${index}][unit_price_cents]`"
              type="number"
              min="0"
              step="1"
              placeholder="100"
              class="w-full"
            />
          </UFormField>

          <div class="flex items-end">
            <UButton
              type="button"
              icon="i-lucide-trash-2"
              color="error"
              variant="ghost"
              square
              @click="removeUsagePrice(index)"
            />
          </div>
        </div>
      </div>

      <div
        v-for="(id, index) in removedUsagePriceIds"
        :key="id"
      >
        <input
          type="hidden"
          :name="`usage_prices_attributes[${usagePrices.length + index}][id]`"
          :value="id"
        >
        <input
          type="hidden"
          :name="`usage_prices_attributes[${usagePrices.length + index}][_destroy]`"
          value="1"
        >
      </div>
    </div>

    <div class="flex items-center gap-3">
      <UButton
        type="submit"
        :label="props.submitLabel"
        icon="i-lucide-save"
        :loading="processing"
      />
    </div>
  </Form>
</template>
